import { Box, Checkbox, Collapse, Divider, Flex, Grid, Input, Text } from '@chakra-ui/react'
import {
  camelCaseToTitleCase,
  colors,
  hasReviewOfSystemsField,
  rosCategories,
  ROSCategory,
} from '@hb/shared'
import { memo, useMemo } from 'react'
import { useField } from 'react-final-form'
import { useFormWidth } from '../../../../components/forms/FinalForm/context'
import { FormElement } from '../../../../components/forms/Input'
import { SectionLabel } from '../../SectionLabel'

const ROSItemInput = memo(({ basePath, itemKey }: { basePath: string; itemKey: string }) => {
  const fieldPath = `${basePath}.${itemKey}`
  const {
    input: { value: current, onChange: onChangeCurrent },
  } = useField<boolean>(`${fieldPath}.current`, {
    subscription: { value: true },
    type: 'checkbox',
  })
  const {
    input: { value: history, onChange: onChangeHistory },
  } = useField<boolean>(`${fieldPath}.history`, {
    subscription: { value: true },
    type: 'checkbox',
  })
  const {
    input: { value: notes, onChange: onChangeNotes },
  } = useField<string>(`${fieldPath}.notes`, {
    subscription: { value: true },
  })

  const isChecked = current || history

  return (
    <Box w="100%">
      <Flex align="center" w="100%" gap={2}>
        <Text
          flex={1}
          minW={0}
          fontSize="sm"
          isTruncated
          fontWeight={isChecked ? 600 : 400}
          color={isChecked ? 'black' : 'gray.600'}>
          {camelCaseToTitleCase(itemKey)}
        </Text>
        <Checkbox colorScheme="green" isChecked={current} onChange={onChangeCurrent} size="sm" />
        <Flex justify="flex-end" w="40px">
          <Checkbox colorScheme="green" isChecked={history} onChange={onChangeHistory} size="sm" />
        </Flex>
      </Flex>
      {isChecked ? (
        <Input
          size="sm"
          mt={0.5}
          _focus={{
            outline: 'none',
            boxShadow: `0 0 2px 1px ${colors.green.hex}`,
            borderColor: 'transparent',
          }}
          placeholder="Notes..."
          value={notes || ''}
          onChange={onChangeNotes}
        />
      ) : null}
    </Box>
  )
})

const ROSNoneCheckbox = ({ basePath }: { basePath: string }) => {
  const {
    input: { value, onChange },
  } = useField<boolean>(`${basePath}.none`, {
    subscription: { value: true },
    type: 'checkbox',
  })
  return (
    <Flex align="center" gap={2} py={1}>
      <Checkbox colorScheme="green" isChecked={value} onChange={onChange} size="sm">
        <Text fontSize="sm" fontWeight={500}>
          None
        </Text>
      </Checkbox>
    </Flex>
  )
}

const ROSCategoryInput = memo(
  ({ category, numColumns }: { category: ROSCategory; numColumns: number }) => {
    const basePath = `data.reviewOfSystem.${category.key}`
    const allItems = useMemo(() => [...category.items, 'other'], [category.items])
    const {
      input: { value: none },
    } = useField<boolean>(`${basePath}.none`, {
      subscription: { value: true },
    })

    const columns = useMemo(() => {
      const cols: string[][] = Array.from({ length: numColumns }, () => [])
      allItems.forEach((item, i) => {
        cols[i % numColumns].push(item)
      })
      return cols
    }, [allItems, numColumns])

    return (
      <Box py={2} px={1} w="100%">
        <Flex align="center" gap={2}>
          <Text fontSize="md" color={colors.green.hex} fontWeight={600}>
            {category.name}
          </Text>
          <ROSNoneCheckbox basePath={basePath} />
        </Flex>
        <Grid
          opacity={none ? 0.5 : 1}
          templateColumns={`repeat(${numColumns}, 1fr)`}
          gap={3}
          w="100%">
          {columns.map((col, colIndex) => (
            <Box key={colIndex}>
              <Flex
                align="center"
                gap={2}
                mb={1}
                borderBottom="1px solid"
                borderColor="gray.200"
                pb={0.5}>
                <Text flex={1} minW={0} fontSize="xs" fontWeight={600} color="gray.500">
                  Symptom
                </Text>
                <Text fontSize="xs" fontWeight={600} color="gray.500" w="auto">
                  Current
                </Text>
                <Text fontSize="xs" fontWeight={600} color="gray.500" w="auto">
                  History
                </Text>
              </Flex>
              <Flex flexFlow="column" gap={1}>
                {col.map(itemKey => (
                  <ROSItemInput key={itemKey} basePath={basePath} itemKey={itemKey} />
                ))}
              </Flex>
            </Box>
          ))}
        </Grid>
      </Box>
    )
  },
)

export const ReviewOfSystemsInput = ({ isLast }: { isLast: boolean }) => {
  const { width } = useFormWidth()
  const {
    input: { value: hasReviewOfSystems },
  } = useField<boolean>('data.hasReviewOfSystems', {
    subscription: { value: true },
    type: 'checkbox',
  })
  const numColumns = useMemo(() => Math.max(1, Math.floor(width / 300)), [width])

  return (
    <Box data-child-name="data.hasReviewOfSystems" w="100%" px={4} py={3}>
      <Box px={1}>
        <FormElement field={hasReviewOfSystemsField} name="data.hasReviewOfSystems" />
      </Box>
      <Collapse style={{ width: '100%' }} in={hasReviewOfSystems}>
        <Box w="100%">
          <SectionLabel>Review of Systems</SectionLabel>
          {rosCategories.map(category => (
            <ROSCategoryInput key={category.key} category={category} numColumns={numColumns} />
          ))}
          {!isLast ? <Divider my={3} /> : null}
        </Box>
      </Collapse>
    </Box>
  )
}
